// UserDownloadsPage.tsx
import React, { FC, useCallback, useContext, useEffect } from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory, useParams } from 'react-router-dom';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { ProductVersionDownloads } from '../components/Downloads/ProductVersionDownloads';
import { useQueryParam } from '../hooks/useQueryParam';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { Nothing } from '../components/Nothing/Nothing';
import { UserDownloadsFetcher } from '../fetchers/UserDownloadsFetcher';
import { IUserDownloadsResponse, IUserDownloadsVersion } from '../resources/models/IUserDownloads';
import { useAutoFetch } from '../contexts/Fetcher';

const DISCOVERY_TAB_VALUE = 'discovery';
// Manifest product projectPath that identifies Pixotope Graphics
const PIXOTOPE_MANIFEST_PROJECT_PATH = 'pixotope';

interface IUrlParams {
  product?: string;
  version?: string;
  productId?: string;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    accordionsContainer: {
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
  })
);

export function shouldShowDiscoveryTab(
  userDownloads: IUserDownloadsResponse['userDownloads'] | null | undefined
): boolean {
  const downloads = userDownloads ?? [];

  const productNames = downloads
    .map((download) => download?.product?.name?.trim().toLowerCase())
    .filter((name): name is string => Boolean(name));

  const hasDiscovery = productNames.some((name) => name.includes('discovery'));
  const hasPixotopeGraphicsAccess = downloads.some(
    (download) =>
      download?.product?.name?.trim().toLowerCase().includes('pixotope') && (download.versions?.length ?? 0) > 0
  );

  return hasDiscovery && !hasPixotopeGraphicsAccess;
}

export const UserDownloadsPage: FC = () => {
  const styles = useStyles();
  const { data, isLoading } = useContext(UserDownloadsFetcher.Context);
  useAutoFetch(UserDownloadsFetcher);

  const { t } = useTranslation();
  const { product: activeProductId, version: expandedVersion } = useParams<IUrlParams>();
  const history = useHistory();
  const productId = parseInt(useQueryParam('productId') ?? '', 10);

  const setActiveTab = useCallback((tab: number | string, replaceState = false) => {
    const url =
      typeof tab === 'string' && isNaN(Number(tab))
        ? `${AuthenticatedRoutes.UserDownloads()}/${tab}`
        : AuthenticatedRoutes.UserDownloadsProduct(tab as number);
    if (replaceState) {
      history.replace(url);
    } else {
      history.push(url);
    }
  }, []);

  const onChange = useCallback((event, tab) => {
    setActiveTab(tab);
  }, []);

  const showDiscovery = shouldShowDiscoveryTab(data?.userDownloads);

  // Discovery download entry from userDownloads (product whose name includes "discovery")
  const discoveryDownload =
    (data?.userDownloads ?? []).find((d) => d.product?.name?.trim().toLowerCase().includes('discovery')) ?? null;

  // Pixotope Graphics product from the manifest — used as the Discovery download source when
  // the user has no Discovery product with versions of their own
  const pixotopeManifestProduct =
    (data?.manifestFile.products ?? []).find(
      (p) => p.projectPath === PIXOTOPE_MANIFEST_PROJECT_PATH || p.projectPath === `${PIXOTOPE_MANIFEST_PROJECT_PATH}/`
    ) ?? null;

  // Map manifest versions → IUserDownloadsVersion so they can be rendered by ProductVersionDownloads.
  // Reversed so the newest version appears first (descending order).
  const manifestPixotopeVersions: IUserDownloadsVersion[] = [...(pixotopeManifestProduct?.versions ?? [])]
    .reverse()
    .map((manifestVersion, vi) => ({
      version: manifestVersion.version,
      tools: false,
      content: false,
      examples: false,
      lens: false,
      installers: manifestVersion.installer.map((inst, ii, allInstallers) => ({
        manifestFileVersion: {
          forceUpdate: inst.forceUpdate,
          version: inst.version,
          released: inst.released,
          changelog: inst.changelog,
          filePath: inst.filePath,
          author: 0,
        },
        // filePath from the manifest is the S3 key — enough for useDownload to fetch a signed URL
        file: inst.filePath ? { Key: inst.filePath, Size: 0, StorageClass: '', LastModified: '', ETag: '' } : undefined,
        lastObject: ii === allInstallers.length - 1,
        // After reversing, vi === 0 is the newest version
        latest: vi === 0 && ii === allInstallers.length - 1,
        capexIncompatibility: false,
      })),
    }));

  const discoveryTabValue = discoveryDownload?.product.id.toString(10);

  // Discovery is handled as a separate conditional tab; exclude it from the regular filtered list
  const filteredDownloads = (data?.userDownloads ?? []).filter(
    (d) => d.versions.length > 0 && !d.product?.name?.trim().toLowerCase().includes('discovery')
  );

  useEffect(() => {
    if (!data) return;

    if (productId) {
      const userDownload = data.userDownloads.find((ud) => ud.product.id === productId);
      if (userDownload) {
        setActiveTab(userDownload.product.id, true);
        return;
      }
    }
    if (!activeProductId) {
      if (filteredDownloads.length) {
        setActiveTab(filteredDownloads[0].product.id, true);
      } else if (showDiscovery) {
        setActiveTab(discoveryTabValue!, true);
      }
    }
  }, [data?.userDownloads, activeProductId, productId]);

  const activeProduct =
    filteredDownloads.find((downloads) => downloads.product.id.toString(10) === activeProductId) ?? null;

  const isDiscoveryTabActive = showDiscovery && activeProductId === discoveryTabValue;

  // Versions shown in the content area when Discovery tab is active:
  // prefer versions from the licensed Discovery product; fall back to manifest Pixotope data
  const discoveryVersions: IUserDownloadsVersion[] = discoveryDownload?.versions.length
    ? discoveryDownload.versions
    : manifestPixotopeVersions;

  // Product details used for routing inside the Discovery tab content
  const discoveryProductDetails =
    discoveryDownload?.product ??
    (pixotopeManifestProduct
      ? { id: parseInt(pixotopeManifestProduct.id, 10), name: 'Pixotope', plans: [], created: 0, stripeProductId: '' }
      : null);

  // Unified content values for whichever tab is active
  const contentVersions = activeProduct?.versions ?? (isDiscoveryTabActive ? discoveryVersions : []);
  const contentProductDetails = activeProduct?.product ?? (isDiscoveryTabActive ? discoveryProductDetails : null);

  const onToggle = useCallback(
    (version: string, value: boolean, replaceState = false) => {
      if (!contentProductDetails) return;
      const historyFn = replaceState ? history.replace : history.push;
      if (value) {
        historyFn(AuthenticatedRoutes.UserDownloadsProductVersion(contentProductDetails.id, version));
      } else {
        historyFn(AuthenticatedRoutes.UserDownloadsProduct(contentProductDetails.id));
      }
    },
    [contentProductDetails?.id, history]
  );

  useEffect(() => {
    if (contentProductDetails && !expandedVersion && contentVersions.length) {
      onToggle(contentVersions[0].version, true, true);
    }
  }, [contentProductDetails?.id]);

  const hasAnyContent = filteredDownloads.length > 0 || showDiscovery;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return data ? (
    hasAnyContent ? (
      <div>
        <PageHeader title={t('downloads.title')} />

        {filteredDownloads.length === 0 && !showDiscovery ? <Nothing title={t('downloads.noDownloads.title')} /> : null}

        {activeProductId && (
          <Tabs
            value={activeProductId}
            indicatorColor="primary"
            textColor="primary"
            onChange={onChange}
            variant="scrollable"
            scrollButtons={'auto'}
          >
            {filteredDownloads.map((downloads) => (
              <Tab
                key={downloads.product.id}
                label={downloads.product.name}
                value={downloads.product.id.toString(10)}
              />
            ))}
            {showDiscovery && <Tab key={DISCOVERY_TAB_VALUE} label="Discovery" value={discoveryTabValue} />}
          </Tabs>
        )}

        <div className={styles.accordionsContainer}>
          {contentProductDetails &&
            contentVersions.map((version) => (
              <ProductVersionDownloads
                key={`${contentProductDetails.id}/${version.version}`}
                product={contentProductDetails}
                version={version}
                expanded={expandedVersion === version.version}
                onToggle={onToggle}
              />
            ))}
        </div>
      </div>
    ) : (
      <Nothing organizationContext={true} />
    )
  ) : null;
};
